import AccountIcon from "@/assets/images/icons/account-icon.svg";
import { ConfirmSignOutDialog } from "@/components/account/ConfirmSignOutDialog";
import { AnnouncementsDropdown } from "@/components/AnnouncementsDropdown";
import { BookmarkManager } from "@/components/BookmarkManager";
import { ExploreDropdown } from "@/components/ExploreDropdown";
import { LiveSessionsDropdown } from "@/components/LiveSessionsDropdown";
import { GlobalSearchOverlayMobile } from "@/components/search/GlobalSearchOverlay";
import { DrawerItem } from "@/components/ui/DrawerItem";
import { URLS } from "@/lib/constants";
import { getBundledAppVersion } from "@/lib/appVersion";
import { useAuth } from "@/lib/AuthContext";
import { isCommunityUrl } from "@/lib/communityLinks";
import { isMemberTier } from "@/lib/membershipTier";
import { useCommunityWebView } from "@/lib/CommunityWebViewContext";
import { useDrawer } from "@/lib/DrawerContext";
import { goHome } from "@/lib/navigation";
import { resolveAppLink } from "@/lib/resolveAppLink";
import { useIsMobile } from "@/lib/useDeviceType";
import { resolveUserHomeUrl } from "@/lib/userHomeUrl";
import { Ionicons } from "@expo/vector-icons";
import { usePathname, useRouter } from "expo-router";
import React, { ReactNode, useState } from "react";
import { Alert, Linking, Platform, Pressable, ScrollView } from "react-native";
import { Drawer } from "react-native-drawer-layout";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { SizableText, Text, XStack, YStack } from "tamagui";

type MobileDrawerProps = {
  children: ReactNode;
};

export function MobileDrawer({ children }: MobileDrawerProps) {
  const {
    isOpen,
    closeDrawer,
    drawerItems,
    openDrawer,
    searchOpen,
    setSearchOpen,
    liveSessionsOpen,
    setLiveSessionsOpen,
    bookmarksOpen,
    setBookmarksOpen,
    announcementsOpen,
    setAnnouncementsOpen,
  } = useDrawer();
  const { user, preferenceMetadata, membershipTier, membershipLoading, signOut } =
    useAuth();
  const communityWebView = useCommunityWebView();
  const router = useRouter();
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const insets = useSafeAreaInsets();
  const isWeb = Platform.OS === "web";
  const [exploreOpen, setExploreOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const homeUrl = resolveUserHomeUrl(
    preferenceMetadata ?? undefined,
    membershipTier,
    membershipLoading,
  );

  // Web only: navigate to the in-app login screen ourselves, otherwise
  // `useWordPressLoginRedirect` bounces to WordPress and signs the user back in
  // from the still-valid WP cookie. On native the (main) guard already redirects
  // once the session clears, so navigating here would render login twice.
  const runSignOut = async () => {
    setSigningOut(true);
    closeDrawer();
    const { error } = await signOut();
    setSigningOut(false);
    setConfirmOpen(false);
    if (error) {
      if (isWeb) {
        if (typeof window !== "undefined") {
          window.alert(error.message || "Failed to sign out");
        }
      } else {
        Alert.alert("Error", error.message || "Failed to sign out");
      }
      return;
    }
    if (isWeb) router.replace("/auth/login" as never);
  };

  // Always render Drawer to keep tree stable across resizes
  const drawerEnabled = isMobile;

  const profileRow = (
    <XStack ai="center" gap="$3" px="$4" py="$4" width="100%">
      <AccountIcon width={30} height={30} color={"white"} />
      <YStack f={1} flexShrink={1}>
        <Text col="white" fow="600" fos="$4">
          Account
        </Text>
        {user?.email && (
          <Text col="white" fos="$2" numberOfLines={1}>{user.email}</Text>
        )}
      </YStack>
      <Ionicons name="chevron-forward" size={20} color="#666" style={{ marginLeft: "auto" }} />
    </XStack>
  );

  return (
    <>
      <Drawer
        open={drawerEnabled && isOpen}
        onOpen={openDrawer}
        onClose={closeDrawer}
        drawerType="front"
        drawerPosition="right"
        drawerStyle={{
          backgroundColor: "#000",
          width: drawerEnabled ? "85%" : 0,
        }}
        renderDrawerContent={() =>
          drawerEnabled ? (
            <YStack f={1} bg="#0D0D0D" pt={insets.top}>
              {/* Drawer Header with Close Button */}
              <XStack ai="center" jc="space-between" px="$4" py="$4" mb="$2">
                <Pressable onPress={closeDrawer} hitSlop={8}>
                  <Ionicons name="close" size={28} color="white" />
                </Pressable>
              </XStack>

              {/* Account identity — only this row keeps icon + chevron. */}
              <Pressable
                onPress={() => {
                  closeDrawer();
                  router.push("/settings" as any);
                }}
              >
                {profileRow}
              </Pressable>

              {/* Membership — same text style as Dashboard / The Hang (no icon). */}
              <YStack px="$4">
                <DrawerItem
                  label="Membership"
                  active={pathname.startsWith("/membership")}
                  onPress={() => {
                    closeDrawer();
                    // Web: the membership lives on the WordPress account page.
                    if (isWeb && typeof window !== "undefined") {
                      window.location.href = URLS.MY_ACCOUNT;
                      return;
                    }
                    router.push("/membership" as any);
                  }}
                />
                {/* Native only: videos saved for offline viewing. */}
                {!isWeb && (
                  <DrawerItem
                    label="My downloads"
                    active={pathname.startsWith("/downloads")}
                    onPress={() => {
                      closeDrawer();
                      router.push("/downloads" as any);
                    }}
                  />
                )}
              </YStack>
              <YStack h={1} bg="#5C5854" mx="$4" my="$2" alignSelf="stretch" />

              {/* Mobile web search entry (native has search in bottom bar) */}
              {isWeb && (
                <YStack px="$4" mt="$1" mb="$3">
                  <Pressable onPress={() => { closeDrawer(); setSearchOpen(true); }}>
                    <XStack ai="center" px="$3" py="$2.5" br={999} bg="#2A2826" gap="$2">
                      <Ionicons name="search" size={18} color="#C9C5C3" />
                      <Text col="#C9C5C3" fos="$3">Search...</Text>
                    </XStack>
                  </Pressable>
                </YStack>
              )}

              {/* Drawer Menu Items */}
              <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false} bounces={false}>
                <YStack gap="$3" px="$4" f={1} pb="$6" pt="$2">
                  {drawerItems
                    // Native: non-subscribed users don't see The Hang at all
                    // (web keeps today's behavior).
                    .filter(
                      (item) =>
                        item.label !== "The Hang" ||
                        isWeb ||
                        membershipLoading ||
                        isMemberTier(membershipTier),
                    )
                    .map((item) => {
                    const isActive = item.href ? pathname.startsWith(item.href) : false;

                    // Explore — inline accordion (Browse All Courses is a top-level drawer item)
                    if (item.label === "Explore") {
                      return (
                        <React.Fragment key={item.label}>
                          <DrawerItem
                            label="Browse All Courses"
                            active={pathname === "/courses"}
                            onPress={() => {
                              closeDrawer();
                              // popToTop back to the existing /courses root when
                              // deeper in; no-op (no remount) when already there.
                              goHome();
                            }}
                          />
                          <DrawerItem
                            label="Browse by Instructor"
                            active={pathname.startsWith("/instructors")}
                            onPress={() => {
                              closeDrawer();
                              router.push("/instructors" as any);
                            }}
                          />
                          <YStack h={1} bg="#5C5854" my="$2" alignSelf="stretch" />
                          <Pressable
                            onPress={() => setExploreOpen((prev) => !prev)}
                            style={{ marginBottom: exploreOpen ? 4 : 0 }}
                          >
                            <XStack ai="center" jc="space-between" py="$3" px="$2">
                              <SizableText
                                color="white"
                                size="$3"
                                fontWeight="600"
                                letterSpacing={1}
                                textTransform="uppercase"
                              >
                                Explore
                              </SizableText>
                              <Ionicons
                                name={exploreOpen ? "chevron-up" : "chevron-down"}
                                size={20}
                                color="#999"
                              />
                            </XStack>
                          </Pressable>
                          {exploreOpen && <ExploreDropdown inDrawer onNavigate={closeDrawer} />}
                          <YStack h={1} bg="#5C5854" my="$3" alignSelf="stretch" />
                        </React.Fragment>
                      );
                    }

                    // Dividers live at section boundaries only:
                    // after Browse (inside Explore fragment) and after Explore.
                    // Dashboard stays with Browse as one nav block — no line under it.
                    // The Hang is last; Sign Out draws its own footer divider.
                    const showDividerAfter = item.label === "Live Sessions";

                    return (
                      <React.Fragment key={item.label}>
                        <DrawerItem
                          icon={item.icon}
                          label={item.label}
                          active={isActive}
                          onPress={() => {
                            closeDrawer();
                            if (item.externalUrl) {
                              const url = item.label === "Dashboard" ? homeUrl : item.externalUrl;
                              if (!isWeb && communityWebView && isCommunityUrl(url)) {
                                // The Hang opens in the in-app autologin WebView.
                                communityWebView.openCommunity(url);
                                return;
                              }
                              const { href, openInApp } = resolveAppLink(url);
                              if (openInApp) {
                                router.push(href as any);
                              } else {
                                void Linking.openURL(href);
                              }
                            } else if (item.onPress) {
                              item.onPress();
                            } else if (item.href) {
                              router.push(item.href as any);
                            }
                          }}
                        />
                        {showDividerAfter && (
                          <YStack h={1} bg="#5C5854" my="$3" alignSelf="stretch" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </YStack>
              </ScrollView>

              <YStack px="$4" pb={insets.bottom + 12} pt="$2" flexShrink={0}>
                {user ? (
                  <>
                    <YStack h={1} bg="#5C5854" mb="$2" alignSelf="stretch" />
                    <Pressable
                      onPress={() => {
                        closeDrawer();
                        setConfirmOpen(true);
                      }}
                      hitSlop={8}
                    >
                      <XStack ai="center" gap="$3" py="$3" px="$2">
                        <Ionicons name="log-out-outline" size={22} color="#C9C5C3" />
                        <Text col="#C9C5C3" fos="$3" fow="600">
                          Sign Out
                        </Text>
                      </XStack>
                    </Pressable>
                  </>
                ) : null}
                <Text
                  col="#666"
                  fos="$1"
                  ta="center"
                  pt={user ? "$2" : 0}
                >
                  App version {getBundledAppVersion()}
                </Text>
              </YStack>
            </YStack>
          ) : null
        }
      >
        <YStack f={1}>{children}</YStack>
      </Drawer>

      {/* Overlays rendered OUTSIDE <Drawer> to avoid native interaction conflicts.
          Triggered from the native bottom bar (and mobile-web header/drawer). */}
      <GlobalSearchOverlayMobile open={searchOpen} onOpenChange={setSearchOpen} />
      {membershipTier !== "non_member" && (
        <LiveSessionsDropdown
          membershipTier={membershipTier}
          membershipLoading={membershipLoading}
          open={liveSessionsOpen}
          onOpenChange={setLiveSessionsOpen}
          hideTrigger
        />
      )}
      <BookmarkManager
        open={bookmarksOpen}
        onOpenChange={setBookmarksOpen}
        hideTrigger
      />
      {user?.id ? (
        <AnnouncementsDropdown
          userId={user.id}
          open={announcementsOpen}
          onOpenChange={setAnnouncementsOpen}
          hideTrigger
        />
      ) : null}

      <ConfirmSignOutDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={() => void runSignOut()}
        busy={signingOut}
      />
    </>
  );
}
